import { baseUrl } from "../utilities/configurations";
import apiRequest from "../utilities/request";
import { useAuth } from "./AuthContext";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";

const TransactionHistoryContext = createContext();

export function useTransactionHistoryContext() {
  return useContext(TransactionHistoryContext);
}

export function TransactionHistoryProvider(props) {
  const initDateRange = {
    selected: true,
    dateFrom: dayjs().subtract(30, "days"),
    dateTo: dayjs(),
    timeFrame: 0,
  };

  const initAppliedFilter = {
    dateRange: initDateRange,
    consultationType: { value: "All", selected: false },
    medicalTestType: { value: "All", selected: false },
    specialization: { value: "All", selected: false },
    claimType: { value: "All", selected: false },
    transType: { value: "All", selected: false },
    status: { value: "All", selected: false },
  };

  const [detailsLoadState, setDetailsLoadState] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [currentScreen, setCurrentScreen] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [isViewing, setIsViewing] = useState(false);

  const [selectedTransType, setSelectedTransType] = useState({
    value: "All",
    selected: false,
  });
  const [selectedDateRange, setSelectedDateRange] = useState(initDateRange);
  const [selectedStatus, setSelectedStatus] = useState({
    value: "All",
    selected: false,
  });
  const [selectedSpecialization, setSelectedSpecialization] = useState({
    value: "All",
    selected: false,
  });
  const [selectedMedicalTestType, setSelectedMedicalTestType] = useState({
    value: "All",
    selected: false,
  });
  const [selectedClaimType, setSelectedClaimType] = useState({
    value: "All",
    selected: false,
  });
  const [selectedConsultationType, setSelectedConsultationType] = useState({
    value: "All",
    selected: false,
  });

  const [appliedFilter, setAppliedFilter] = useState(initAppliedFilter);
  const [filterCount, setFilterCount] = useState(0);

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [medicalTestTypeList, setMedicalTestTypeList] = useState([]);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [specializationList, setSpecializationList] = useState([]);
  const [loadState, setLoadState] = useState("");
  const { userData, setUserData } = useAuth();

  useEffect(() => {
    let allConsultations = transactionHistory.filter((e) => {
      return e.consultationType === "Consultation" && e.specialization !== "";
    });
    let specs = allConsultations.map((e) => e.specialization);
    setSpecializationList([...new Set(specs)]);

    let allProcedures = transactionHistory.filter((e) => {
      return e.consultationType === "Procedures" && e.medicalTestType !== "";
    });
    let testTypes = allProcedures.map((e) => e.medicalTestType);
    setMedicalTestTypeList([...new Set(testTypes)]);
  }, [transactionHistory]);

  const handleBackToPolicies = () => {
    resetFilter();
    setAppliedFilter(initAppliedFilter);
    setCurrentScreen("choose policy");
  };

  const initTransactions = (state, selected) => {
    let params =
      getTransTypeParam(selectedTransType, selectedConsultationType) +
      getDateRangeParam(selectedDateRange) +
      getStatusParam(selectedStatus) +
      getSpecializationParam(selectedSpecialization) +
      getMedicalTestParam(selectedMedicalTestType);

    getTransactionHistory(params, state, selected);
  };

  const getTransactionHistory = async (params, state, selected) => {
    try {
      setLoadState(state || "load");
      const res = await apiRequest(
        baseUrl + `/transaction/history?cardNo=${selected?.cardNo}${params}`,
        "GET"
      );
      if (!res.error) {
        setTransactionHistory(res.data || []);
        setLoadState("load success");
      } else if (res.status >= 500) {
        setLoadState("ISE");
      } else {
        setLoadState("load failed");
      }
    } catch (error) {
      setLoadState("timeout");
    }
  };

  const handleSelectPolicy = (selected) => {
    setSelectedPolicy(selected);
    setCurrentScreen("transactions");
    initTransactions(null, selected);
  };

  const getPolicy = async (cardNo) => {
    //from dashboard your medical test redirect
    //if policies already loaded, skip get policy
    setDetailsLoadState("load");
    if (userData.length > 0) {
      getTransactionItem(userData, cardNo);

      let index = userData.findIndex((e) => e.cardNo === cardNo);
      if (index >= 0) {
        setSelectedPolicy(userData[index]);
        initTransactions(null, userData[index]);
      }
    } else {
      try {
        const res = await apiRequest(baseUrl + `/policy/policies`, "GET");
        if (!res.error) {
          let data = res.data || [];
          setUserData(data);
          getTransactionItem(data, cardNo);

          let index = data.findIndex((e) => e.cardNo === cardNo);
          if (index >= 0) {
            setSelectedPolicy(data[index]);
            initTransactions(null, data[index]);
          }
        }
      } catch (error) {
        console.log(error);
        setCurrentScreen("choose policy");
        setDetailsLoadState("load failed");
      }
    }
  };

  const getTransactionItem = async (policies, cardNo) => {
    let index = policies.findIndex((e) => {
      return e.cardNo === cardNo;
    });
    if (index >= 0) {
      setSelectedPolicy(policies[index]);
      try {
        const res = await apiRequest(
          baseUrl + `/transaction/transaction-item${window.location.search}`
        );
        if (!res.error) {
          setSelectedTransaction(res.data);
          setDetailsLoadState("load success");
        } else {
          setCurrentScreen("choose policy");
          setDetailsLoadState("load failed");
        }
      } catch (error) {
        console.log(error);
        setCurrentScreen("choose policy");
        setDetailsLoadState("load failed");
      }
    } else {
      setCurrentScreen("choose policy");
      setDetailsLoadState("load failed");
    }
  };

  const handleShowFilter = () => {
    setSelectedTransType(appliedFilter.transType);
    setSelectedDateRange(appliedFilter.dateRange);
    setSelectedStatus(appliedFilter.status);
    setShowFilters(true);
  };

  const resetFilter = () => {
    setSelectedTransType(initAppliedFilter.transType);
    setSelectedDateRange(initDateRange);
    setSelectedStatus(initAppliedFilter.status);
    setSelectedSpecialization(initAppliedFilter.specialization);
    setSelectedMedicalTestType(initAppliedFilter.medicalTestType);
    setSelectedClaimType(initAppliedFilter.claimType);
    setSelectedConsultationType(initAppliedFilter.consultationType);
  };

  const handleCloseFilter = () => {
    resetFilter();
    setShowFilters(false);
  };

  const handleSelectTransType = (tType) => {
    setSelectedTransType({ value: tType, selected: tType !== "All" });
    setSelectedStatus(initAppliedFilter.status);
    setSelectedSpecialization(initAppliedFilter.specialization);
    setSelectedMedicalTestType(initAppliedFilter.medicalTestType);
    setSelectedClaimType(initAppliedFilter.claimType);
    setSelectedConsultationType(initAppliedFilter.consultationType);
  };

  const handleChangeDateFrom = (dt) => {
    let dTo = selectedDateRange.dateTo;
    if (
      selectedDateRange.dateTo &&
      dayjs(dt).isAfter(dayjs(selectedDateRange.dateTo))
    ) {
      dTo = dt;
    }
    setSelectedDateRange({
      ...selectedDateRange,
      dateFrom: dt,
      dateTo: dTo,
      selected: Boolean(dt && dTo),
    });
  };

  const handleChangeDateTo = (dt) => {
    let dF = selectedDateRange.dateFrom;
    if (
      selectedDateRange.dateFrom &&
      dayjs(dt).isBefore(dayjs(selectedDateRange.dateFrom))
    ) {
      dF = dt;
    }
    setSelectedDateRange({
      ...selectedDateRange,
      dateFrom: dF,
      dateTo: dt,
      selected: Boolean(dF && dt),
    });
  };

  const handleSelectTimeFrame = (tf) => {
    if (selectedDateRange.timeFrame !== tf) {
      setSelectedDateRange({
        dateFrom: dayjs().subtract(tf, "days"),
        dateTo: dayjs(),
        timeFrame: tf,
        selected: true,
      });
    }
  };

  const handleSelectStatus = (status) => {
    setSelectedStatus({ value: status, selected: status !== "All" });
  };

  const handleSelectSpecialization = (status) => {
    setSelectedSpecialization({ value: status, selected: status !== "All" });
  };

  const handleSelectMedicalTestType = (status) => {
    setSelectedMedicalTestType({ value: status, selected: status !== "All" });
  };

  const handleSelectClaimType = (status) => {
    setSelectedClaimType({ value: status, selected: status !== "All" });
  };

  const handleSelectConsultationType = (status) => {
    setSelectedConsultationType({ value: status, selected: status !== "All" });
  };

  const getTransTypeParam = (item, cType) => {
    let str = "";
    if (item.selected) {
      if (item.value === "PCC Consultation") {
        str = "&consultationType=Consultation";
      } else if (item.value === "PCC Medical Test") {
        str = "&consultationType=Procedures";
      } else if (item.value === "Letter of Authorization") {
        str =
          "&consultationType=Letter of Authorization" +
          (cType.value !== "All"
            ? " - " + cType.value
            : "");
      }
    }
    return str;
  };

  const getDateRangeParam = (item) => {
    let str = "";
    if (item.selected) {
      str = `&startDate=${dayjs(item.dateFrom).format(
        "YYYY-MM-DD"
      )}&endDate=${dayjs(item.dateTo).format("YYYY-MM-DD")}`;
    }
    return str;
  };

  const getStatusParam = (item) => {
    return `&status=${item.value}`;
  };

  const getSpecializationParam = (item) => {
    let str = "";
    if (item.selected) {
      str = `&specialization=${item.value}`;
    }
    return str;
  };

  const getMedicalTestParam = (item) => {
    let str = "";
    if (item.selected) {
      str = `&medicalTestType=${item.value}`;
    }
    return str;
  };

  const handleApplyFilter = () => {
    setAppliedFilter({
      dateRange: selectedDateRange,
      transType: selectedTransType,
      status: selectedStatus,
      specialization: selectedSpecialization,
      medicalTestType: selectedMedicalTestType,
      claimType: selectedClaimType,
      consultationType: selectedConsultationType,
    });
    let params =
      getTransTypeParam(selectedTransType, selectedConsultationType) +
      getDateRangeParam(selectedDateRange) +
      getStatusParam(selectedStatus) +
      getSpecializationParam(selectedSpecialization) +
      getMedicalTestParam(selectedMedicalTestType);

    getTransactionHistory(params, null, selectedPolicy);
    setShowFilters(false);
  };

  useEffect(() => {
    countFilters();
  }, [appliedFilter]);

  const countFilters = () => {
    let selectedFilters = Object.keys(appliedFilter).filter(
      (f) => appliedFilter[f].selected
    );
    setFilterCount(selectedFilters.length);
  };

  const handleClearFilter = () => {
    resetFilter();
    setAppliedFilter(initAppliedFilter);
    setShowFilters(false);
    let params = getStatusParam(initAppliedFilter.status);
    getTransactionHistory(params, null, selectedPolicy);
  };

  const handleClickTransaction = (item) => {
    setSelectedTransaction(item);
    setCurrentScreen("details");
  };

  const handleRemoveFilter = (item) => {
    let newFilter = JSON.parse(JSON.stringify(appliedFilter));
    newFilter[item].selected = false;
    if (item === "transType") {
      newFilter[item].value = "All";
      setSelectedTransType(initAppliedFilter.transType);
      // Remove filters related to this
      newFilter["status"] = { value: "All", selected: false };
      setSelectedStatus(initAppliedFilter.status);
      newFilter["specialization"] = { value: "All", selected: false };
      setSelectedSpecialization(initAppliedFilter.specialization);
      newFilter["medicalTestType"] = { value: "All", selected: false };
      setSelectedMedicalTestType(initAppliedFilter.medicalTestType);
      newFilter["claimType"] = { value: "All", selected: false };
      setSelectedClaimType(initAppliedFilter.claimType);
      newFilter["consultationType"] = { value: "All", selected: false };
      setSelectedConsultationType(initAppliedFilter.consultationType);
    } else if (item === "dateRange") {
      newFilter[item] = initDateRange;
      setSelectedDateRange(initDateRange);
    } else if (item === "status") {
      newFilter[item].value = "All";
      setSelectedStatus(initAppliedFilter.status);
    } else if (item === "specialization") {
      newFilter[item].value = "All";
      setSelectedSpecialization(initAppliedFilter.specialization);
    } else if (item === "medicalTestType") {
      newFilter[item].value = "All";
      setSelectedMedicalTestType(initAppliedFilter.medicalTestType);
    } else if (item === "claimType") {
      newFilter[item].value = "All";
      setSelectedClaimType(initAppliedFilter.claimType);
    } else if (item === "consultationType") {
      newFilter[item].value = "All";
      setSelectedConsultationType(initAppliedFilter.consultationType);
    }
    setAppliedFilter(newFilter);
    let params =
      getTransTypeParam(newFilter.transType, newFilter.consultationType) +
      getDateRangeParam(newFilter.dateRange) +
      getStatusParam(newFilter.status) +
      getSpecializationParam(newFilter.specialization) +
      getMedicalTestParam(newFilter.medicalTestType);

    getTransactionHistory(params, null, selectedPolicy);
    countFilters();
  };

  const hasValidDateRange =
    (selectedDateRange.dateFrom == null && selectedDateRange.dateTo == null) ||
    Boolean(selectedDateRange.dateFrom && selectedDateRange.dateTo);

  const transactionTypes = [
    "PCC Consultation",
    "PCC Medical Test",
    "Letter of Authorization",
    "Reimbursement (Coming Soon)",
  ];

  const transactionStatusList = [
    "Pending",
    "Completed",
    "Cancelled",
    "No Show",
  ];

  const loaStatusList = [
    "Approved",
    "For Approval",
    "Cancelled",
    "Disapproved",
  ];

  const claimTypeList = [
    "In Patient (IP)",
    "Out Patient (OP)",
    "Optical",
    "Dental",
    "OP Medicine",
  ];

  const consultationTypeList = ["Consultation", "Medical Test"];

  const data = {
    handleSelectConsultationType,
    handleSelectMedicalTestType,
    handleSelectSpecialization,
    handleClickTransaction,
    setSelectedTransaction,
    handleSelectTransType,
    handleSelectClaimType,
    handleSelectTimeFrame,
    getTransactionHistory,
    setSelectedTransType,
    handleChangeDateFrom,
    handleBackToPolicies,
    setDetailsLoadState,
    handleSelectPolicy,
    handleRemoveFilter,
    handleSelectStatus,
    handleChangeDateTo,
    setSelectedStatus,
    handleCloseFilter,
    handleApplyFilter,
    handleClearFilter,
    setCurrentScreen,
    handleShowFilter,
    initTransactions,
    setIsViewing,
    getPolicy,

    selectedConsultationType,
    selectedMedicalTestType,
    selectedSpecialization,
    transactionStatusList,
    consultationTypeList,
    selectedTransaction,
    medicalTestTypeList,
    specializationList,
    transactionHistory,
    selectedClaimType,
    selectedTransType,
    selectedDateRange,
    hasValidDateRange,
    detailsLoadState,
    transactionTypes,
    selectedPolicy,
    selectedStatus,
    claimTypeList,
    appliedFilter,
    currentScreen,
    loaStatusList,
    showFilters,
    filterCount,
    isViewing,
    loadState,
  };

  const memoData = useMemo(
    () => data,
    [
      selectedConsultationType,
      selectedMedicalTestType,
      selectedSpecialization,
      selectedTransaction,
      transactionHistory,
      selectedTransType,
      selectedDateRange,
      selectedClaimType,
      hasValidDateRange,
      detailsLoadState,
      selectedPolicy,
      selectedStatus,
      appliedFilter,
      currentScreen,
      filterCount,
      showFilters,
      loadState,
    ]
  );

  return (
    <TransactionHistoryContext.Provider value={memoData}>
      {props.children}
    </TransactionHistoryContext.Provider>
  );
}

TransactionHistoryProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
